/* ==========================================================================
   VI Knowledge Region Map — "Länder & regioner" atlas
   --------------------------------------------------------------------------
   A knowledge-adapted variant of the storefront origin map
   (snippets/vi-origin-map.liquid). It reuses that visual/interaction pattern
   — Leaflet via CDN, CARTO Voyager tiles, count markers, a side panel and
   country chips — but is DATA-DRIVEN from the PUBLIC knowledge index
   (region ARTICLES), never from products or customer data.

   It is intentionally namespaced (vi-region-map-* / data-vi-region-*) and uses
   a per-instance canvas id so it never collides with the origin map
   (#vi-origin-leaflet-canvas).

   The panel (country chips + region search + region cards) is the robust core
   and works WITHOUT Leaflet. The map itself is a progressive enhancement: if
   Leaflet fails to load, a fallback note is shown and the panel still works.

   Region cards carry data-vi-open="<slug>"; the in-root knowledge engine
   (vi-knowledge-hub.js) handles opening the reader/modal — this file never
   opens the reader itself. No new external dependency: Leaflet is already used
   by the origin map and is loaded from the same CDN with a window.L guard.
   ========================================================================== */
(function () {
  'use strict';

  var LEAFLET_JS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.js';
  var LEAFLET_CSS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.css';

  // Country centroids — reused from the origin map (Swedish + English keys).
  var COORDS = {
    'frankrike': [46.2, 2.2], 'france': [46.2, 2.2],
    'italien': [42.8, 12.5], 'italy': [42.8, 12.5],
    'spanien': [40.4, -3.7], 'spain': [40.4, -3.7],
    'portugal': [39.6, -8.0],
    'tyskland': [51.0, 10.0], 'germany': [51.0, 10.0],
    'österrike': [47.6, 14.2], 'osterrike': [47.6, 14.2], 'austria': [47.6, 14.2],
    'grekland': [38.6, 23.5], 'greece': [38.6, 23.5],
    'usa': [38.5, -98.0], 'united states': [38.5, -98.0],
    'chile': [-35.0, -71.2],
    'argentina': [-33.5, -68.6],
    'australien': [-33.8, 138.6], 'australia': [-33.8, 138.6],
    'nya zeeland': [-41.5, 173.5], 'nya-zeeland': [-41.5, 173.5], 'new zealand': [-41.5, 173.5],
    'sydafrika': [-33.7, 19.0], 'south africa': [-33.7, 19.0]
  };

  // Swedish country tokens that may appear in a region's tags → display name.
  var TAG_COUNTRY = {
    'frankrike': 'Frankrike', 'italien': 'Italien', 'spanien': 'Spanien',
    'portugal': 'Portugal', 'tyskland': 'Tyskland', 'osterrike': 'Österrike',
    'grekland': 'Grekland', 'usa': 'USA', 'chile': 'Chile',
    'argentina': 'Argentina', 'australien': 'Australien', 'sydafrika': 'Sydafrika',
    'nya-zeeland': 'Nya Zeeland'
  };

  // Regions whose tags carry NO country token → explicit country (FAS 3 mapping).
  // Documented in docs/knowledge-region-country-mapping.md.
  var SLUG_COUNTRY = {
    'barossa-valley': 'Australien', 'casablanca-valley': 'Chile', 'maipo-valley': 'Chile',
    'marlborough': 'Nya Zeeland', 'mendoza': 'Argentina', 'napa-valley': 'USA',
    'sonoma': 'USA', 'willamette-valley': 'USA', 'stellenbosch': 'Sydafrika',
    'santorini': 'Grekland', 'douro': 'Portugal', 'mosel': 'Tyskland',
    'pfalz': 'Tyskland', 'rheingau': 'Tyskland', 'wachau': 'Österrike'
  };

  // Display order for country chips (known wine countries first, rest A–Ö).
  var COUNTRY_ORDER = ['Frankrike', 'Italien', 'Spanien', 'Portugal', 'Tyskland',
    'Österrike', 'Grekland', 'USA', 'Chile', 'Argentina', 'Australien',
    'Nya Zeeland', 'Sydafrika'];

  // ── helpers ───────────────────────────────────────────────────────────────
  function asciiFold(v) {
    var s = String(v == null ? '' : v);
    if (typeof s.normalize === 'function') s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s;
  }
  function slugify(v) {
    return String(v == null ? '' : v).toLowerCase().trim()
      .replace(/[^a-z0-9\u0080-\uffff]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function foldKey(v) { return asciiFold(String(v || '')).toLowerCase().trim().replace(/\s+/g, '-'); }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function toArray(v) {
    if (Array.isArray(v)) return v.filter(function (x) { return x != null && x !== ''; });
    if (typeof v === 'string') return v.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    return [];
  }
  function debounce(fn, wait) {
    var t; return function () { var c = this, a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); };
  }

  // ── pure data layer (exposed for QA) ───────────────────────────────────────
  // Derive a region's country from the slug-override map, then a tag token.
  function deriveCountry(region) {
    if (!region) return '';
    var slug = slugify(region.slug || '');
    if (SLUG_COUNTRY[slug]) return SLUG_COUNTRY[slug];
    var tags = toArray(region.tags);
    for (var i = 0; i < tags.length; i++) {
      var key = foldKey(tags[i]);
      if (TAG_COUNTRY[key]) return TAG_COUNTRY[key];
    }
    return '';
  }

  // Build [{ country, regions:[...] }] sorted by COUNTRY_ORDER then count.
  function groupByCountry(regions) {
    var map = {};
    for (var i = 0; i < regions.length; i++) {
      var c = regions[i].country || 'Övriga';
      (map[c] = map[c] || []).push(regions[i]);
    }
    var keys = Object.keys(map);
    keys.sort(function (a, b) {
      var ia = COUNTRY_ORDER.indexOf(a), ib = COUNTRY_ORDER.indexOf(b);
      if (ia < 0) ia = 999; if (ib < 0) ib = 999;
      if (ia !== ib) return ia - ib;
      if (map[b].length !== map[a].length) return map[b].length - map[a].length;
      return a.localeCompare(b, 'sv');
    });
    return keys.map(function (k) {
      map[k].sort(function (a, b) { return String(a.title).localeCompare(String(b.title), 'sv'); });
      return { country: k, regions: map[k] };
    });
  }

  // Filter regions by a free-text query (title / slug / country / tags).
  function filterRegions(regions, query, country) {
    var q = String(query || '').toLowerCase().trim();
    var toks = q ? q.split(/\s+/).filter(Boolean) : [];
    return regions.filter(function (r) {
      if (country && r.country !== country) return false;
      if (!toks.length) return true;
      var hay = (r.title + ' ' + r.slug + ' ' + (r.country || '') + ' ' + toArray(r.tags).join(' ')).toLowerCase();
      for (var i = 0; i < toks.length; i++) if (hay.indexOf(toks[i]) < 0) return false;
      return true;
    });
  }

  // Normalise raw public-index entries → region records (regions only).
  function buildRegions(data) {
    var arr = (data && Array.isArray(data.items)) ? data.items : (Array.isArray(data) ? data : []);
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var raw = arr[i];
      var cat = foldKey(raw.category || raw.type || '');
      if (cat !== 'regioner' && cat !== 'region') continue;
      var rec = {
        slug: slugify(raw.slug || raw.handle || raw.title),
        title: String(raw.title || raw.name || raw.slug || ''),
        excerpt: String(raw.excerpt || raw.summary || ''),
        tags: toArray(raw.tags)
      };
      rec.country = deriveCountry(rec);
      if (rec.slug) out.push(rec);
    }
    return out;
  }

  // ── Leaflet loader (reuses the origin-map pattern; window.L guard) ─────────
  function loadLeaflet(cb, onFail) {
    if (window.L) { cb(); return; }
    if (!document.querySelector('link[data-vi-leaflet-css]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = LEAFLET_CSS;
      link.setAttribute('data-vi-leaflet-css', '');
      document.head.appendChild(link);
    }
    var s = document.createElement('script');
    s.src = LEAFLET_JS;
    s.onload = function () { cb(); };
    s.onerror = function () { if (onFail) onFail(); };
    document.head.appendChild(s);
  }

  // ── instance ───────────────────────────────────────────────────────────────
  function initMap(root) {
    if (!root || root.getAttribute('data-vi-region-ready') === '1') return;
    root.setAttribute('data-vi-region-ready', '1');

    var cfgEl = root.querySelector('[data-vi-knowledge-map-config]');
    var cfg = {};
    try { cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {}; } catch (e) { cfg = {}; }
    cfg = cfg || {};

    var labels = cfg.labels || {};
    var L_ALL = labels.all || 'Alla länder';
    var L_REGIONS = labels.regions || 'regioner';
    var L_REGION = labels.region || 'region';
    var L_READ = labels.read || 'Läs regionguide';
    var L_EMPTY = labels.empty || 'Inga regioner matchar sökningen.';

    var elSearch = root.querySelector('[data-vi-region-search]');
    var elChips = root.querySelector('[data-vi-region-chips]');
    var elCards = root.querySelector('[data-vi-region-cards]');
    var elCount = root.querySelector('[data-vi-region-count]');
    var elFallback = root.querySelector('[data-vi-region-fallback]');
    var elCanvas = root.querySelector('[data-vi-region-canvas]');

    var state = { regions: [], groups: [], country: null, query: '', map: null, markers: {} };

    function countLabel(n) {
      return n + ' ' + (n === 1 ? L_REGION : L_REGIONS);
    }

    function renderChips() {
      if (!elChips) return;
      var html = '<button type="button" class="vi-region-map__chip vi-region-map__chip--all" data-vi-region-country="" aria-pressed="' +
        (state.country ? 'false' : 'true') + '"><span class="vi-region-map__chip-label">' + escapeHtml(L_ALL) +
        '</span><span class="vi-region-map__chip-count">' + state.regions.length + '</span></button>';
      for (var i = 0; i < state.groups.length; i++) {
        var g = state.groups[i];
        html += '<button type="button" class="vi-region-map__chip" data-vi-region-country="' + escapeHtml(g.country) +
          '" aria-pressed="' + (state.country === g.country ? 'true' : 'false') + '"><span class="vi-region-map__chip-label">' +
          escapeHtml(g.country) + '</span><span class="vi-region-map__chip-count">' + g.regions.length + '</span></button>';
      }
      elChips.innerHTML = html;
    }

    function renderCards() {
      if (!elCards) return;
      var list = filterRegions(state.regions, state.query, state.country)
        .sort(function (a, b) { return String(a.title).localeCompare(String(b.title), 'sv'); });
      if (elCount) elCount.textContent = countLabel(list.length);
      if (!list.length) {
        elCards.innerHTML = '<p class="vi-region-map__empty">' + escapeHtml(L_EMPTY) + '</p>';
        return;
      }
      var html = '';
      for (var i = 0; i < list.length; i++) {
        var r = list[i];
        html += '<button type="button" class="vi-region-map__card" data-vi-open="' + escapeHtml(r.slug) + '">' +
          (r.country ? '<span class="vi-region-map__card-country">' + escapeHtml(r.country) + '</span>' : '') +
          '<span class="vi-region-map__card-title">' + escapeHtml(r.title) + '</span>' +
          '<span class="vi-region-map__card-link" aria-hidden="true">' + escapeHtml(L_READ) + '</span></button>';
      }
      elCards.innerHTML = html;
    }

    function syncChipState() {
      if (!elChips) return;
      var chips = elChips.querySelectorAll('[data-vi-region-country]');
      for (var i = 0; i < chips.length; i++) {
        var val = chips[i].getAttribute('data-vi-region-country') || '';
        chips[i].setAttribute('aria-pressed', (val === (state.country || '')) ? 'true' : 'false');
      }
    }

    function syncMarkers() {
      if (!state.map) return;
      Object.keys(state.markers).forEach(function (k) {
        var el = state.markers[k].getElement();
        if (!el) return;
        var inner = el.querySelector('.vi-region-map__marker');
        if (inner) inner.classList.toggle('is-active', state.country != null && foldKey(k) === foldKey(state.country));
      });
    }

    function setCountry(country) {
      state.country = country || null;
      syncChipState();
      syncMarkers();
      renderCards();
      if (state.map && state.country && state.markers[state.country]) {
        var m = state.markers[state.country];
        try { state.map.flyTo(m.getLatLng(), Math.max(state.map.getZoom(), 4), { duration: 0.5 }); } catch (e) { /* noop */ }
      }
    }

    function bind() {
      if (elSearch) {
        elSearch.addEventListener('input', debounce(function () {
          state.query = elSearch.value || '';
          renderCards();
        }, 160));
      }
      if (elChips) {
        elChips.addEventListener('click', function (ev) {
          var chip = ev.target.closest('[data-vi-region-country]');
          if (!chip) return;
          var val = chip.getAttribute('data-vi-region-country') || '';
          setCountry(val === (state.country || '') ? null : val);
        });
      }
    }

    function buildMap() {
      if (!elCanvas || !window.L) { showFallback(); return; }
      var map;
      try {
        map = window.L.map(elCanvas, {
          zoomControl: false, scrollWheelZoom: false, attributionControl: true, minZoom: 2, maxZoom: 10
        });
      } catch (e) { showFallback(); return; }
      state.map = map;
      window.L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd', maxZoom: 12
      }).addTo(map);

      var latlngs = [];
      for (var i = 0; i < state.groups.length; i++) {
        var g = state.groups[i];
        var ll = COORDS[foldKey(g.country)];
        if (!ll) continue;
        latlngs.push(ll);
        var n = g.regions.length;
        var sz = n >= 20 ? 50 : n >= 12 ? 44 : n >= 6 ? 38 : n >= 3 ? 32 : 26;
        var fs = Math.round(Math.max(11, sz * 0.34));
        var icon = window.L.divIcon({
          className: 'vi-region-map__marker-wrap',
          html: '<div class="vi-region-map__marker" style="width:' + sz + 'px;height:' + sz + 'px;font-size:' + fs + 'px">' + n +
            '</div><span class="vi-region-map__marker-label">' + escapeHtml(g.country) + '</span>',
          iconSize: [sz, sz + 16], iconAnchor: [sz / 2, sz / 2]
        });
        var marker = window.L.marker(ll, { icon: icon, keyboard: false }).addTo(map);
        (function (country) { marker.on('click', function () { setCountry(state.country === country ? null : country); }); })(g.country);
        state.markers[g.country] = marker;
      }

      if (latlngs.length) {
        try { map.fitBounds(window.L.latLngBounds(latlngs), { padding: [40, 40], maxZoom: 5 }); }
        catch (e) { map.setView([42, 6], 4); }
      } else {
        map.setView([42, 6], 4);
      }

      var zin = root.querySelector('[data-vi-region-zoom-in]');
      var zout = root.querySelector('[data-vi-region-zoom-out]');
      var zfit = root.querySelector('[data-vi-region-zoom-fit]');
      if (zin) zin.addEventListener('click', function () { map.zoomIn(); });
      if (zout) zout.addEventListener('click', function () { map.zoomOut(); });
      if (zfit) zfit.addEventListener('click', function () {
        if (latlngs.length) { try { map.fitBounds(window.L.latLngBounds(latlngs), { padding: [40, 40], maxZoom: 5 }); } catch (e) { /* noop */ } }
      });
      if (elCanvas) elCanvas.removeAttribute('data-vi-region-empty');
    }

    function showFallback() {
      if (elFallback) elFallback.hidden = false;
      if (elCanvas) elCanvas.setAttribute('data-vi-region-empty', '');
    }

    function paint(data) {
      state.regions = buildRegions(data);
      state.groups = groupByCountry(state.regions);
      renderChips();
      renderCards();
      bind();
      // Progressive enhancement: attempt the Leaflet map; panel already works.
      if (elCanvas) {
        loadLeaflet(buildMap, showFallback);
      }
    }

    // Fetch the PUBLIC knowledge index (region articles). Browser cache dedupes
    // this with the hub engine's own fetch of the same URL.
    var urls = toArray(cfg.searchIndex);
    (function attempt(i) {
      if (i >= urls.length) { showFallback(); if (elCount) elCount.textContent = ''; return; }
      fetch(urls[i], { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (res) { if (!res.ok) throw new Error('status ' + res.status); return res.json(); })
        .then(function (data) { paint(data); })
        .catch(function () { attempt(i + 1); });
    })(0);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-vi-knowledge-map]');
    for (var i = 0; i < roots.length; i++) initMap(roots[i]);
  }

  window.ViKnowledgeRegionMap = window.ViKnowledgeRegionMap || {};
  window.ViKnowledgeRegionMap.init = initAll;
  window.ViKnowledgeRegionMap.deriveCountry = deriveCountry;
  window.ViKnowledgeRegionMap.groupByCountry = groupByCountry;
  window.ViKnowledgeRegionMap.filterRegions = filterRegions;
  window.ViKnowledgeRegionMap.buildRegions = buildRegions;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (ev) { if (ev && ev.target) initAll(ev.target); });

  /* ══════════════════════════════════════════════════════════════════════
     MOBILE COMPACT (additiv modul — ändrar inga befintliga funktioner)
     ------------------------------------------------------------------------
     Kartscenen döljs ≤749px av CSS (vi-knowledge-region-map.css,
     "MOBILKOMPAKT LAGER"); panelen (sök + landval + regionkort) fungerar
     precis som förut eftersom den aldrig var beroende av Leaflet. Det enda
     mobilbeteende som kräver JS: en Leaflet-karta som initierats medan
     scenen var dold har fel storlek/utsnitt när läget växlar till desktop.
     Vid övergången över 749px triggas därför Leaflets egen resize-hantering
     (window resize → invalidateSize) plus den BEFINTLIGA "Visa alla
     länder"-kontrollen ([data-vi-region-zoom-fit]) som återställer utsnittet
     — utan att kartlogiken skrivs om. Gated bakom matchMedia med
     change-lyssnare; utan matchMedia (QA-shimmen) är modulen en no-op.
     ══════════════════════════════════════════════════════════════════════ */
  (function () {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (typeof window.matchMedia !== 'function' || !document.querySelectorAll) return;
    var mql;
    try { mql = window.matchMedia('(max-width: 749px)'); } catch (e) { mql = null; }
    if (!mql) return;

    function refitOnDesktop(ev) {
      var mobile = !!(ev && typeof ev.matches === 'boolean' ? ev.matches : mql.matches);
      if (mobile) return; // in i mobil-läget: kartan döljs av CSS, inget att göra
      window.setTimeout(function () {
        try { window.dispatchEvent(new Event('resize')); } catch (e) { /* äldre webbläsare */ }
        var fits = document.querySelectorAll('[data-vi-knowledge-map] [data-vi-region-zoom-fit]');
        for (var i = 0; i < fits.length; i++) {
          try { fits[i].click(); } catch (e) { /* noop */ }
        }
      }, 120);
    }
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', refitOnDesktop);
    else if (typeof mql.addListener === 'function') mql.addListener(refitOnDesktop);
  })();
})();
